import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  AssetReservation,
  PaginatedResponse,
  CreateReservationPayload,
} from '@/lib/types';

export interface ReservationFilters {
  page?: number;
  page_size?: number;
  asset?: number | '';
  reserved_from_after?: string;
  reserved_from_before?: string;
  reserved_until_after?: string;
  reserved_until_before?: string;
  ordering?: string;
}

export function useReservations(filters: ReservationFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.reservations.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<AssetReservation>> => {
      const response = await apiClient.get<PaginatedResponse<AssetReservation>>(
        '/api/v1/asset-reservations/',
        { params: cleanFilters }
      );
      return response.data;
    },
  });
}

export function useCreateReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateReservationPayload): Promise<AssetReservation> => {
      const response = await apiClient.post<AssetReservation>(
        '/api/v1/asset-reservations/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useUpdateReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateReservationPayload>;
    }): Promise<AssetReservation> => {
      const response = await apiClient.patch<AssetReservation>(
        `/api/v1/asset-reservations/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useDeleteReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/asset-reservations/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}
